import { BrandKit } from "@redotech/redo-model/brand-kit";
import {
  BaseElementType,
  LogoElement,
  MediaUploadData,
  MediaUploadElement,
  ProductDetailsElement,
  RichTextElement,
  ShortAnswerData,
  ShortAnswerElement,
  SingleSelectData,
  SingleSelectElement,
  StarsData,
  StarsElement,
} from "@redotech/redo-model/content-builder";
import { groupInput, input, InputProvider, listInput } from "@redotech/ui/form";
import { deepCopyObject } from "@redotech/util/object";
import { assertNever } from "@redotech/util/type";
import { WidgetEditorProps } from "../form-builder/widget-editors/widget-editor-decorator";

let uniqueNumberGenerator = 1;

export type WriteableElement =
  | WithId<ShortAnswerElement>
  | WithId<StarsElement>
  | WithId<MediaUploadElement>
  | WithId<SingleSelectElement>;

export type PossibleElements =
  | WriteableElement
  | RichTextElement
  | ProductDetailsElement
  | LogoElement
  | MediaUploadElement
  | SingleSelectElement;

type WithId<T> = T & { _id: string };

export const PossibleBaseElements = [
  BaseElementType.SHORT_ANSWER,
  BaseElementType.RICH_TEXT,
  BaseElementType.PRODUCT_DETAILS,
  BaseElementType.LOGO,
  BaseElementType.STARS,
  BaseElementType.MEDIA_UPLOAD,
  BaseElementType.SINGLE_SELECT,
] as const;
export type PossibleBaseElements = (typeof PossibleBaseElements)[number];

export type PossibleSubmittableData =
  | ShortAnswerData
  | StarsData
  | MediaUploadData
  | SingleSelectData;

export type ReviewElementProps<T extends PossibleElements> = {
  element: T;
};

export interface ReviewElementEditorProps<T extends PossibleElements>
  extends WidgetEditorProps<ReviewElementProps<T>, T> {
  widget: ReviewElementProps<T>;
}

export interface Page {
  elements: PossibleElements[];
}

export enum ReviewsFormGlobalTabs {
  OVERVIEW = "Overview",
  STYLES = "Styles",
}

const pageInput = groupInput({
  elements: input<PossibleElements[]>(),
});

const pageInputDefault = () => {
  return {
    elements: [],
  };
};

const pagesInput = listInput(
  () => pageInput,
  pageInputDefault,
  (page, index) => index,
);

export const reviewsForm = groupInput({
  settings: groupInput({
    title: input<string>(),
    description: input<string>(),
    categories: input<string[]>(),
  }),
  styles: groupInput({
    brandKitOverride: input<BrandKit>(),
    brandKitEnabled: input<boolean>(),
  }),
  pages: pagesInput,
});

export type PageValue = InputProvider.Value<typeof pageInput>;
export type PageForm = InputProvider.Form<typeof pageInput>;
export type PagesForm = InputProvider.Form<typeof pagesInput>;

export type ReviewsFormValue = InputProvider.Value<typeof reviewsForm>;
export type ReviewsFormForm = InputProvider.Form<typeof reviewsForm>;

const defaultShortAnswer = (): WithId<ShortAnswerElement> => {
  return {
    type: BaseElementType.SHORT_ANSWER,
    required: false,
    title: "Short answer question",
    placeholderText: "Briefly describe your experience",
    _id: `${uniqueNumberGenerator++}`,
  };
};

const defaultProductDetails = (): ProductDetailsElement => {
  return {
    type: BaseElementType.PRODUCT_DETAILS,
    imageWidthPercentage: 50,
  };
};

const defaultLogo = (): LogoElement => ({
  type: BaseElementType.LOGO,
  widthPercentage: 50,
});

const defaultStars = (): WithId<StarsElement> => ({
  type: BaseElementType.STARS,
  title: "Review the product",
  required: true,
  _id: `${uniqueNumberGenerator++}`,
});

export function getReviewsFormDefault(brandKit: BrandKit): ReviewsFormValue {
  const brandKitOverride = deepCopyObject(brandKit);
  brandKitOverride.images.logoUrl =
    "https://redoapi-prod.s3.amazonaws.com/generic-logo.png";

  return {
    settings: {
      title: "Review form",
      description: "This form is sent to customers to collect reviews",
      categories: [],
    },
    styles: {
      brandKitOverride: brandKitOverride,
      brandKitEnabled: false,
    },
    pages: [
      {
        elements: [defaultLogo(), defaultProductDetails(), defaultStars()],
      },
      {
        elements: [
          getRichText(brandKit, { html: "<p>Leave a review</p>" }),
          {
            type: BaseElementType.SHORT_ANSWER,
            title: "Title of your review",
            required: true,
            placeholderText: "",
            _id: `${uniqueNumberGenerator++}`,
          },
        ],
      },
      {
        elements: [
          getRichText(brandKit, { html: "<p>Upload a photo or video</p>" }),
          defaultMediaUpload(),
        ],
      },
      {
        elements: [
          getRichText(brandKit, {
            html: "<p>Would you recommend this product?</p>",
          }),
          {
            type: BaseElementType.SINGLE_SELECT,
            required: true,
            options: [
              "Yes, I would recommend this product",
              "No, I would not recommend this product",
            ],
            _id: `${uniqueNumberGenerator++}`,
          },
        ],
      },
    ],
  };
}

const defaultMediaUpload = (): WithId<MediaUploadElement> => ({
  type: BaseElementType.MEDIA_UPLOAD,
  widthPercentage: 50,
  _id: `${uniqueNumberGenerator++}`,
  required: false,
});

const defaultSingleSelect = (): WithId<SingleSelectElement> => ({
  type: BaseElementType.SINGLE_SELECT,
  options: ["Option A", "Option B"],
  _id: `${uniqueNumberGenerator++}`,
  required: false,
});

export const getRichText = (
  brandKit: BrandKit,
  overrides: Partial<RichTextElement> = {},
): RichTextElement => {
  const fontFamily = brandKit.font.fontFamily;
  const fontSize = brandKit.font.hierarchy.h3.fontSizePx;
  const fontWeight = brandKit.font.hierarchy.h3.fontWeight;

  const defaultValue: RichTextElement = {
    id: uniqueNumberGenerator++,
    type: BaseElementType.RICH_TEXT,
    html: "<p></p>",
    fontFamily,
    fontWeight: fontWeight,
    fontSizePx: fontSize,
    textColor: "#000000",
    linkColor: "#0000ff",
  };

  return { ...defaultValue, ...overrides };
};

export function getDefaultElement(
  type: PossibleBaseElements,
  brandKit: BrandKit,
): PossibleElements {
  switch (type) {
    case BaseElementType.SHORT_ANSWER:
      return defaultShortAnswer();
    case BaseElementType.RICH_TEXT:
      return getRichText(brandKit);
    case BaseElementType.PRODUCT_DETAILS:
      return defaultProductDetails();
    case BaseElementType.LOGO:
      return defaultLogo();
    case BaseElementType.STARS:
      return defaultStars();
    case BaseElementType.MEDIA_UPLOAD:
      return defaultMediaUpload();
    case BaseElementType.SINGLE_SELECT:
      return defaultSingleSelect();
    default:
      assertNever(type);
  }
}

export function elementIsWriteable(
  element: PossibleElements,
): element is WriteableElement {
  return (element as WriteableElement)._id !== undefined;
}
